import { newRegistration, deleteRegistration } from "../../actions/registrationActions";
import { overrideAttempt, overrideEntryId, setLiftingGroup } from "../../actions/liftingActions";
import { newDefaultEntry } from "../../logic/entry";
import type { AnyAction } from "redux";
import type { ThunkAction } from "redux-thunk";
import type { Entry, Equipment, Event, Lift, Sex } from "../../types/dataTypes";
import type { GlobalState } from "../../types/stateTypes";
import { setWrpfSession } from "../handoff/wrpfSessionActions";
import { wrpfBodyweightForWeightClass, wrpfWeightClassFromBodyweight } from "../handoff/modalityMapping";
import type { WrpfCurrentRecord, WrpfEntryAnchor } from "../handoff/types";
import { setRecordMarkingMode } from "../live/platformLiveProfileActions";

export const RECORD_TEST_DATA_NOTE = "WRPF_RECORD_TEST_DATA";
const RECORD_TEST_ID_PREFIX = "wrpf-record-test:";

export type RecordTestScenario = "below" | "equal" | "above";

export type RecordTestCase = {
  readonly scenario: RecordTestScenario;
  readonly label: string;
  readonly weightKg: number;
  readonly entry: Entry;
  readonly anchor: WrpfEntryAnchor;
};

export type RecordTestData = {
  readonly day: number;
  readonly platform: number;
  readonly lift: Lift;
  readonly cases: ReadonlyArray<RecordTestCase>;
};

type RecordTestThunk = ThunkAction<void, GlobalState, undefined, AnyAction>;

const scenarioDefinitions: ReadonlyArray<{
  scenario: RecordTestScenario;
  label: string;
  deltaKg: number;
}> = [
  { scenario: "below", label: "Debajo", deltaKg: -2.5 },
  { scenario: "equal", label: "Igual", deltaKg: 0 },
  { scenario: "above", label: "Supera", deltaKg: 2.5 },
];

const liftForRecord = (record: WrpfCurrentRecord): Lift => {
  if (record.liftType === "SQUAT") return "S";
  if (record.liftType === "BENCH_PRESS") return "B";
  return "D";
};

const equipmentForRecord = (record: WrpfCurrentRecord): Equipment => {
  if (record.equipment === "CLASSIC") return "Wraps";
  if (record.equipment === "SINGLE_PLY") return "Single-ply";
  if (record.equipment === "MULTI_PLY") return "Multi-ply";
  return "Sleeves";
};

const sexForRecord = (record: WrpfCurrentRecord): Sex => {
  const sex = record.sex.toUpperCase();
  if (sex.includes("WOMAN") || sex.includes("FEMALE") || sex === "F") return "F";
  if (sex.includes("MX") || sex.includes("NON_BINARY")) return "Mx";
  return "M";
};

const locationForRecord = (record: WrpfCurrentRecord): { country: string; state: string } => {
  const stateByRegion: Partial<Record<WrpfCurrentRecord["jurisdiction"], string>> = {
    NOA: "Tucumán",
    NEA: "Misiones",
    CUYO: "Mendoza",
    PAMPEANA: "Buenos Aires",
    PATAGONICA: "Chubut",
  };
  return { country: "Argentina", state: stateByRegion[record.jurisdiction] ?? "Tucumán" };
};

// Los récords se evalúan en la categoría del pesaje: el atleta de prueba tiene que pesar dentro de la
// del récord. Sirve el peso del titular si cae ahí; si no (estándar vacante, dato viejo), el límite.
const bodyweightForRecord = (record: WrpfCurrentRecord, sex: Sex): number => {
  const incumbentKg = record.bodyweightKg;
  if (incumbentKg !== null && wrpfWeightClassFromBodyweight(incumbentKg, sex) === record.weightClass) {
    return incumbentKg;
  }
  return wrpfBodyweightForWeightClass(record.weightClass) ?? incumbentKg ?? 75;
};

const setAttemptWeight = (entry: Entry, lift: Lift, weightKg: number): void => {
  if (lift === "S") entry.squatKg[0] = weightKg;
  if (lift === "B") entry.benchKg[0] = weightKg;
  if (lift === "D") entry.deadliftKg[0] = weightKg;
};

const roundDownToAttemptIncrement = (weightKg: number): number => Math.floor(weightKg / 2.5) * 2.5;

const setRecordTestAttempts = (entry: Entry, record: WrpfCurrentRecord, targetKg: number): void => {
  if (record.liftType !== "TOTAL") {
    setAttemptWeight(entry, liftForRecord(record), targetKg);
    return;
  }

  if (record.discipline === "SBD") {
    entry.squatKg[0] = roundDownToAttemptIncrement(targetKg * 0.35);
    entry.squatStatus[0] = 1;
  }
  entry.benchKg[0] = roundDownToAttemptIncrement(targetKg * (record.discipline === "SBD" ? 0.2 : 0.4));
  entry.benchStatus[0] = 1;
  entry.deadliftKg[0] = targetKg - entry.squatKg[0] - entry.benchKg[0];
};

export const recordTestWeights = (record: WrpfCurrentRecord): ReadonlyArray<number> =>
  scenarioDefinitions.map(({ deltaKg }) => Math.max(2.5, record.weightKg + deltaKg));

export const buildRecordTestData = (params: {
  record: WrpfCurrentRecord;
  firstEntryId: number;
  day: number;
  platform: number;
}): RecordTestData => {
  const { record, firstEntryId, day, platform } = params;
  const lift = liftForRecord(record);
  const location = locationForRecord(record);
  const equipment = equipmentForRecord(record);
  const cases = scenarioDefinitions.map(({ scenario, label, deltaKg }, index): RecordTestCase => {
    const id = firstEntryId + index;
    const weightKg = Math.max(2.5, record.weightKg + deltaKg);
    const entry = newDefaultEntry(id);
    Object.assign(entry, {
      day,
      platform,
      flight: "T",
      name: `Prueba récord · ${label}`,
      sex: sexForRecord(record),
      country: location.country,
      state: location.state,
      equipment,
      divisions: [record.division],
      events: [record.discipline as Event],
      lot: index + 1,
      memberId: `${RECORD_TEST_ID_PREFIX}${id}`,
      team: "Datos de prueba Records",
      notes: RECORD_TEST_DATA_NOTE,
      bodyweightKg: bodyweightForRecord(record, sexForRecord(record)),
    });
    setRecordTestAttempts(entry, record, weightKg);
    const anchor: WrpfEntryAnchor = {
      openlifterEntryId: id,
      userOnTournamentId: `${RECORD_TEST_ID_PREFIX}${id}`,
      userId: `${RECORD_TEST_ID_PREFIX}user:${id}`,
      primaryEvent: `${record.discipline}_${record.equipment}`,
      primaryDivision: record.division,
      weightClass: record.weightClass,
      sex: record.sex,
    };
    return { scenario, label, weightKg, entry, anchor };
  });
  return { day, platform, lift, cases };
};

const isGeneratedAnchor = (anchor: WrpfEntryAnchor): boolean =>
  anchor.userOnTournamentId.startsWith(RECORD_TEST_ID_PREFIX);

export const isRecordTestEntry = (entry: Entry): boolean =>
  entry.notes === RECORD_TEST_DATA_NOTE && entry.memberId.startsWith(RECORD_TEST_ID_PREFIX);

export const generateRecordTestData =
  (record: WrpfCurrentRecord): RecordTestThunk =>
  (dispatch, getState) => {
    const before = getState();
    for (const entry of before.registration.entries) {
      if (isRecordTestEntry(entry)) dispatch(deleteRegistration(entry.id));
    }

    const state = getState();
    const day = state.wrpfLive.activeProfile?.day ?? state.lifting.day;
    const platform = state.wrpfLive.activeProfile?.platformId ?? state.lifting.platform;
    const data = buildRecordTestData({
      record,
      firstEntryId: state.registration.nextEntryId,
      day,
      platform,
    });
    for (const testCase of data.cases) dispatch(newRegistration(testCase.entry));

    dispatch(
      setWrpfSession({
        ...state.wrpfSession,
        entryAnchors: state.wrpfSession.entryAnchors
          .filter((anchor) => !isGeneratedAnchor(anchor))
          .concat(data.cases.map((testCase) => testCase.anchor)),
      }),
    );
    dispatch(setLiftingGroup(day, platform, "T", data.lift));
    dispatch(overrideAttempt(1));
    const activeTestCase = data.cases.find((testCase) => testCase.scenario === "above");
    if (activeTestCase !== undefined) dispatch(overrideEntryId(activeTestCase.entry.id));
    dispatch(setRecordMarkingMode("automatic"));
  };

export const removeRecordTestData = (): RecordTestThunk => (dispatch, getState) => {
  const state = getState();
  for (const entry of state.registration.entries) {
    if (isRecordTestEntry(entry)) dispatch(deleteRegistration(entry.id));
  }
  dispatch(
    setWrpfSession({
      ...state.wrpfSession,
      entryAnchors: state.wrpfSession.entryAnchors.filter((anchor) => !isGeneratedAnchor(anchor)),
    }),
  );
};
